import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {useEffect, useState} from "react";

import {useRevaTranslation} from "../hooks/languageHooks";
import {requestPasswordResetThunk} from "../redux/passwordResetThunk";
import {useRevaDispatch} from "../redux/revaHooks";
import {resolveUserApiErrorMessage} from "../util/errorUtil";
import {handlePreventDefault} from "../util/events";

/** reva-rest's rate-limit status for POST /password-reset/request (see the QualifiedErrorMessageModel body). */
const STATUS_TOO_MANY_REQUESTS = 429;

interface ForgotPasswordDialogProps {
    visible: boolean;
    onHide: () => void;
}

/**
 * #19351: "Passwort vergessen" dialog opened from {@link LoginForm}. POSTs /password-reset/request with no
 * credentials (see `PasswordResetRequestApiInterface`/`requestPasswordResetThunk`) and always shows the
 * same neutral success message — the endpoint itself always answers 200 regardless of whether the
 * account/e-mail exists, so the UI must not leak that distinction either.
 */
export default function ForgotPasswordDialog({visible, onHide}: ForgotPasswordDialogProps) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    const [login, setLogin] = useState("");
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset all state whenever the dialog is (re-)opened, so a previous run never leaks into the next.
    useEffect(() => {
        if (!visible) return;
        setLogin("");
        setSending(false);
        setSuccess(false);
        setError(null);
    }, [visible]);

    const onSubmit = async () => {
        if (!login || sending) return;
        setSending(true);
        setError(null);
        try {
            await dispatch(requestPasswordResetThunk(login));
            setSuccess(true);
        } catch (e) {
            setError(
                resolveUserApiErrorMessage(
                    e,
                    language,
                    t("passwordReset.request.unavailable"),
                    t("general.error.unspecific"),
                    STATUS_TOO_MANY_REQUESTS,
                ),
            );
        } finally {
            setSending(false);
        }
    };

    const footer = (
        <div className="flex justify-end">
            <Button id="forgotPasswordCloseBtn" text label={t("passwordReset.request.backToLogin")} onClick={onHide} />
        </div>
    );

    return (
        <Dialog
            id="forgotPasswordDialog"
            header={t("passwordReset.request.header")}
            visible={visible}
            onHide={onHide}
            style={{width: "28rem"}}
            footer={footer}
        >
            <div className="flex flex-col gap-4">
                <Message
                    id="forgotPasswordHint"
                    severity="info"
                    text={t("passwordReset.request.hint")}
                    className="w-full"
                />
                {!success && (
                    <form className="flex flex-col gap-3" onSubmit={handlePreventDefault(() => void onSubmit())}>
                        <div>
                            <label htmlFor="forgotPasswordLogin" className="block font-medium mb-1">
                                {t("passwordReset.request.login")}
                            </label>
                            <InputText
                                id="forgotPasswordLogin"
                                className="w-full"
                                value={login}
                                disabled={sending}
                                onChange={(e) => setLogin(e.target.value)}
                            />
                        </div>
                        <Button
                            id="forgotPasswordSubmitBtn"
                            label={t("passwordReset.request.submit")}
                            loading={sending}
                            disabled={sending || !login}
                        />
                        {error && <Message id="forgotPasswordError" severity="error" text={error} className="w-full" />}
                    </form>
                )}
                {success && (
                    <Message
                        id="forgotPasswordSuccess"
                        severity="success"
                        text={t("passwordReset.request.success")}
                        className="w-full"
                    />
                )}
            </div>
        </Dialog>
    );
}
